import { useEffect, useState, type FC } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { IconButton } from 'react-native-paper';
import type { SharedValue } from 'react-native-reanimated';

import { LeftIcon, RightIcon } from '@/assets';
import { isWeb } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const BUTTON_SIZE = 60;

const styles = StyleSheet.create({
  container: { flex: 1 },
  arrowButton: {
    margin: 0,
    position: 'absolute',
    top: '50%',
    transform: [{ translateY: -BUTTON_SIZE / 2 }]
  },
  leftButton: { left: 0 },
  rightButton: { right: 0 }
});

type Props = {
  enableArrows?: boolean;
  progress: SharedValue<number>;
  size: number;
  onPressLeft: (index: number) => void;
  onPressRight: (index: number) => void;
} & ViewProps;

export const CarouselWrapper: FC<Props> = ({
  enableArrows = true,
  progress,
  size,
  onPressLeft,
  onPressRight,
  ...wrapperProps
}) => {
  const theme = useAppTheme();

  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(size > 1);

  useEffect(() => {
    if (isWeb) {
      progress.addListener(0, (value) => {
        setShowLeft(value > 0);
        setShowRight(value < size - 1);
      });
    }
  });

  return (
    <View {...wrapperProps} style={[styles.container, wrapperProps?.style]}>
      {wrapperProps.children}
      {isWeb && enableArrows && showLeft && (
        <IconButton
          size={BUTTON_SIZE}
          style={[styles.arrowButton, styles.leftButton]}
          iconColor={theme.colors.onPrimary}
          icon={({ color, size }) => (
            <LeftIcon
              fill={color}
              width={size}
              height={size}
              stroke={theme.colors.primary}
            />
          )}
          onPress={() => onPressLeft(progress.get() - 1)}
        />
      )}

      {isWeb && enableArrows && showRight && (
        <IconButton
          size={BUTTON_SIZE}
          style={[styles.arrowButton, styles.rightButton]}
          iconColor={theme.colors.onPrimary}
          icon={({ color, size }) => (
            <RightIcon
              fill={color}
              width={size}
              height={size}
              stroke={theme.colors.primary}
            />
          )}
          onPress={() => onPressRight(progress.get() + 1)}
        />
      )}
    </View>
  );
};
